import React, { useMemo, useState } from 'react';
import { MarketplaceHeader } from '../components/layout/MarketplaceHeader';
import { Footer } from '../components/layout/Footer';
import { HeroSection } from '../components/marketplace/HeroSection';
import { CategoryChips } from '../components/marketplace/CategoryChips';
import { RestaurantCard } from '../components/marketplace/RestaurantCard';
import { useMarketplaceData } from '../lib/hooks';
import { EmptyState } from '../components/ui/EmptyState';
import { Store } from 'lucide-react';
import { useLanguage } from '../lib/i18n';
export function HomePage() {
  const {
    categories,
    featuredRestaurants,
    allRestaurants,
    restaurantCategoryMap,
    zones,
    loading
  } = useMarketplaceData();
  const [activeCategory, setActiveCategory] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const {
    t
  } = useLanguage();
  // Filter restaurants by category and search
  const filterRestaurants = useMemo(() => {
    return (restaurants: typeof allRestaurants) => {
      let filtered = restaurants;
      // Filter by category
      if (activeCategory) {
        filtered = filtered.filter(r => {
          const cats = restaurantCategoryMap[r.id];
          return cats && cats.includes(activeCategory);
        });
      }
      // Filter by search
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        filtered = filtered.filter(r => r.name.toLowerCase().includes(q) || r.description_short && r.description_short.toLowerCase().includes(q));
      }
      return filtered;
    };
  }, [activeCategory, searchQuery, restaurantCategoryMap]);
  const filteredFeatured = useMemo(() => filterRestaurants(featuredRestaurants), [filterRestaurants, featuredRestaurants]);
  const filteredAll = useMemo(() => filterRestaurants(allRestaurants), [filterRestaurants, allRestaurants]);
  const isFiltering = !!activeCategory || !!searchQuery.trim();
  return <div className="min-h-screen w-full flex flex-col bg-[#0B0B0B]" data-id="element-4251">
      <MarketplaceHeader showPromo data-id="element-4252" />

      <main className="flex-1" data-id="element-4253">
        <HeroSection searchQuery={searchQuery} onSearchChange={setSearchQuery} data-id="element-4254" />

        <div className="max-w-7xl mx-auto py-8" data-id="element-4255">
          {/* Categories */}
          {loading ? <div className="px-4 sm:px-6 lg:px-8 mb-12 flex gap-4 overflow-x-auto no-scrollbar" data-id="element-4256">
              {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="h-12 w-32 rounded-full shrink-0 bg-[#1a1a1a] animate-pulse" data-id="element-4257" />)}
            </div> : <div className="mb-12" data-id="element-4258">
              <CategoryChips categories={categories} activeCategory={activeCategory} onSelect={setActiveCategory} data-id="element-4259" />
            </div>}

          {/* Featured Section — hide when filtering yields 0 featured */}
          {filteredFeatured.length > 0 && <section className="px-4 sm:px-6 lg:px-8 mb-16" data-id="element-4260">
              <div className="flex items-center justify-between mb-8" data-id="element-4261">
                <h2 className="text-2xl font-bold text-white flex items-center gap-3" data-id="element-4262">
                  <span className="w-1.5 h-7 bg-[#C9A34E] rounded-full" data-id="element-4263" />
                  {t('featured')} {t('restaurants')}
                </h2>
              </div>

              {loading ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-4264">
                  {[1, 2, 3].map(i => <div key={i} className="h-[300px] rounded-2xl bg-[#1a1a1a] animate-pulse" data-id="element-4265" />)}
                </div> : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-4266">
                  {filteredFeatured.map(restaurant => <RestaurantCard key={restaurant.id} restaurant={restaurant} zoneName={zones.find(z => z.id === restaurant.zone_id)?.name} data-id="element-4267" />)}
                </div>}
            </section>}

          {/* All Restaurants Section */}
          <section className="px-4 sm:px-6 lg:px-8 mb-16" data-id="element-4268">
            <div className="flex items-center justify-between mb-8" data-id="element-4269">
              <div data-id="element-4270">
                <h2 className="text-2xl font-bold text-white flex items-center gap-3" data-id="element-4271">
                  <span className="w-1.5 h-7 bg-[#C9A34E] rounded-full" data-id="element-4272" />
                  {isFiltering ? 'Resultados' : t('all_restaurants')}
                </h2>
                {!isFiltering && <p className="text-sm text-white/40 mt-1 pl-5" data-id="element-4273">
                    Cada restaurante gestiona sus pedidos directamente
                  </p>}
                {isFiltering && <p className="text-sm text-white/40 mt-1 pl-5" data-id="element-4274">
                    {filteredAll.length} restaurante
                    {filteredAll.length !== 1 ? 's' : ''} encontrado
                    {filteredAll.length !== 1 ? 's' : ''}
                  </p>}
              </div>
            </div>

            {loading ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-4275">
                {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="h-[300px] rounded-2xl bg-[#1a1a1a] animate-pulse" data-id="element-4276" />)}
              </div> : filteredAll.length > 0 ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-4277">
                {filteredAll.map(restaurant => <RestaurantCard key={restaurant.id} restaurant={restaurant} zoneName={zones.find(z => z.id === restaurant.zone_id)?.name} data-id="element-4278" />)}
              </div> : <div className="bg-[#141414] border border-white/10 rounded-2xl p-12 text-center" data-id="element-4279">
                <Store className="w-10 h-10 text-white/20 mx-auto mb-3" data-id="element-4280" />
                <p className="text-white/60 font-medium" data-id="element-4281">
                  {isFiltering ? 'No se encontraron restaurantes con esos filtros' : 'No se encontraron restaurantes'}
                </p>
                <p className="text-white/30 text-sm mt-1" data-id="element-4282">
                  {isFiltering ? 'Prueba con otra categoría o término de búsqueda.' : 'Aún no hay restaurantes disponibles. Vuelve pronto.'}
                </p>
                {isFiltering && <button onClick={() => {
              setActiveCategory('');
              setSearchQuery('');
            }} className="mt-4 px-6 py-2 rounded-full bg-[#C9A34E]/10 text-[#C9A34E] text-sm font-semibold border border-[#C9A34E]/30 hover:bg-[#C9A34E]/20 transition-colors" data-id="element-4283">
                    Limpiar filtros
                  </button>}
              </div>}
          </section>
        </div>
      </main>

      <Footer data-id="element-4284" />
    </div>;
}